import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from 'react-router-dom';
import db from "./Firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function CastrolCertificateList() {
    const moment = extendMoment(Moment);
    const [certificates, setCertificates] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedAt, setSelectedAt] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();

    useEffect(() => {
        const storedUsername = localStorage.getItem("email");
        const storedPassword = localStorage.getItem("password");
        const storedRemember = localStorage.getItem("remember");
        if (storedRemember === "false" || !storedUsername || !storedPassword) {
            navigate('/signin');
        }
    }, [navigate]);

    useEffect(() => {
        const q = query(collection(db, "castrol_certificates_data"), orderBy("timestamp", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setCertificates(snapshot.docs.map(doc => ({
                id: doc.id,
                data: doc.data(),
            })));
        });
        return () => unsubscribe();
    }, []);

    const uniqueLocations = [...new Set(certificates.map(({ data }) => data.at))].filter(Boolean).sort();

    const filtered = certificates.filter(({ data }) => {
        const searchable = `
            ${data.name || ""}
            ${data.mobile || ""}
            ${data.certno || ""}
            ${data.traineeid || ""}
            ${data.batchid || ""}
            ${data.at || ""}
            ${data.from || ""}
            ${data.to || ""}
            ${data.dateissue || ""}
        `.toLowerCase();

        const matchesSearch = searchable.includes(searchTerm.toLowerCase());
        const matchesAt = selectedAt === "" || data.at === selectedAt;
        return matchesSearch && matchesAt;
    });

    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentRows = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / rowsPerPage);

    const handleChangePage = (newPage) => {
        if (newPage < 1 || newPage > totalPages) return;
        setCurrentPage(newPage);
    };

    const handleExportExcel = () => {
        const exportData = filtered.map(({ data }) => ({
            Name: data.name,
            Mobile: data.mobile,
            Type: data.type,
            "Conducted From": data.from,
            "To": data.to,
            "At (Location)": data.at,
            "Certificate No.": data.certno,
            "Trainee ID": data.traineeid,
            "Date of Issuance": data.dateissue,
            "Batch ID": data.batchid,
            Timestamp: data.timestamp
                ? moment(data.timestamp.toDate ? data.timestamp.toDate() : data.timestamp).format("DD/MM/YYYY, h:mm A")
                : "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Castrol Certificates");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
        saveAs(blob, `Castrol_Certificates_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    Castrol EKLAVYA Certificates &nbsp;
                    <span className="text-muted" style={{ fontSize: "13px", fontWeight: 400 }}>
                        Filtered: {filtered.length} / Total: {certificates.length}
                    </span>
                </h5>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* Filters */}
            <div className="row mb-3 g-2">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by name, mobile, cert no, trainee ID…"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={selectedAt}
                        onChange={(e) => { setSelectedAt(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Locations</option>
                        {uniqueLocations.map((loc, idx) => (
                            <option key={idx} value={loc}>{loc}</option>
                        ))}
                    </select>
                </div>

                <div className="col-md-1">
                    <button
                        className="btn btn-secondary w-100"
                        onClick={() => { setSearchTerm(""); setSelectedAt(""); setCurrentPage(1); }}
                    >
                        Reset
                    </button>
                </div>

                <div className="col-md-2">
                    <button
                        className="btn btn-success w-100"
                        onClick={handleExportExcel}
                        disabled={filtered.length === 0}
                    >
                        Export Excel
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle" style={{ fontSize: "13px" }}>
                    <thead className="table-dark">
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Mobile</th>
                            <th>Type</th>
                            <th>Conducted From</th>
                            <th>To</th>
                            <th>At (Location)</th>
                            <th>Certificate No.</th>
                            <th>Trainee ID</th>
                            <th>Date of Issuance</th>
                            <th>Batch ID</th>
                            <th>Timestamp</th>
                            <th>View</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.length === 0 ? (
                            <tr>
                                <td colSpan="13" className="text-center py-4 text-muted">No certificates found</td>
                            </tr>
                        ) : currentRows.map(({ id, data }, idx) => (
                            <tr key={id}>
                                <td>{indexOfFirst + idx + 1}</td>
                                <td>{data.name || "-"}</td>
                                <td>{data.mobile || "-"}</td>
                                <td>{data.type || "-"}</td>
                                <td>{data.from || "-"}</td>
                                <td>{data.to || "-"}</td>
                                <td>{data.at || "-"}</td>
                                <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{data.certno || "-"}</td>
                                <td style={{ whiteSpace: "nowrap" }}>{data.traineeid || "-"}</td>
                                <td>{data.dateissue || "-"}</td>
                                <td style={{ whiteSpace: "nowrap" }}>{data.batchid || "-"}</td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                    {data.timestamp
                                        ? moment(data.timestamp.toDate ? data.timestamp.toDate() : data.timestamp).format("DD/MM/YYYY, h:mm A")
                                        : "N/A"}
                                </td>
                                <td>
                                    <a
                                        href={`/view_castrol_certificate.html?id=${id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-info btn-sm text-white"
                                    >
                                        View
                                    </a>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Rows per page:
                    <select
                        className="form-select d-inline-block w-auto ms-2"
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                    >
                        {[10, 20, 50, 100, 500].map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <button
                        className="btn btn-outline-secondary me-2"
                        onClick={() => handleChangePage(currentPage - 1)}
                        disabled={currentPage === 1}
                    >Previous</button>
                    <span>Page {currentPage} of {totalPages || 1}</span>
                    <button
                        className="btn btn-outline-secondary ms-2"
                        onClick={() => handleChangePage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                    >Next</button>
                </div>
            </div>
        </div>
    );
}

export default CastrolCertificateList;
